import { EnvConfig, Environments } from 'src/configurations'
import Cookies, { CookieSetOptions } from 'universal-cookie'

/**
 * EX-40885: hardening flags shared by every cookie this app writes.
 *
 * `secure` is conditional on the page origin rather than hardcoded true. Every
 * deployed environment is https (`src/server.ts` redirects http to https), so this
 * resolves to true everywhere that matters. Local dev serves plain http on
 * localhost, and a browser that does not exempt localhost from the secure-cookie
 * rule would DROP these writes silently -- login would break with no error to
 * explain it. The check is evaluated per call, not at module scope, because this
 * module is also imported by the SSR bundle where `window` does not exist.
 *
 * `sameSite: 'lax'` and deliberately NOT 'strict': the SSR pass reads the session
 * cookies off the incoming request to hydrate an authed render (`src/server.ts`).
 * Strict cookies are withheld on cross-site top-level navigations, so the SSO
 * hosted-UI return, 3DS/Chargebee returns, and email deep links would each
 * server-render logged out. Lax still blocks the cross-site subrequests that
 * matter for CSRF.
 */
const secureCookieDefaults = (): CookieSetOptions => ({
  secure:
    typeof window === 'undefined' || window.location.protocol === 'https:',
  sameSite: 'lax',
})

const persistentCookieOptions = (): CookieSetOptions => ({
  path: '/',
  expires: new Date('January 1, 3000 00:00:00'),
  ...secureCookieDefaults(),
})

/** Cookies that should die with the browser session carry no expiry. */
const sessionCookieOptions = (): CookieSetOptions => ({
  path: '/',
  ...secureCookieDefaults(),
})

export const getUserPoolClientIdCookieName = (env: Environments): string =>
  `extend_aws_amplify_client_id_${env}`

export const getUserPoolIdCookieName = (env: Environments): string =>
  `extend_aws_amplify_user_pool_id_${env}`

/**
 * The single writer for the SSO user-pool cookies.
 *
 * These were previously written by two duplicated call sites (UserProvider and
 * cognito.utils), each spreading the hardening flags inline. The SSO callback
 * cannot complete without these cookies, so a path that quietly dropped the flags
 * would reopen EX-40885 while every test still passed. One writer means there is
 * one place to get it right and one place to test.
 */
export const setUserPoolCookies = (
  env: Environments,
  {
    userPoolClientId,
    userPoolId,
  }: { userPoolClientId: string; userPoolId: string },
): void => {
  const cookies = new Cookies()
  cookies.set(
    getUserPoolClientIdCookieName(env),
    userPoolClientId,
    sessionCookieOptions(),
  )
  cookies.set(getUserPoolIdCookieName(env), userPoolId, sessionCookieOptions())
}

export const removeUserPoolCookies = (env: Environments): void => {
  const cookies = new Cookies()
  cookies.remove(getUserPoolClientIdCookieName(env), { path: '/' })
  cookies.remove(getUserPoolIdCookieName(env), { path: '/' })
}

export enum CookieNames {
  App = 'app',
  Authed = 'authed',
  Home = 'home',
  YourCardsOverview = 'your_cards_overview',
  YourCardsInactive = 'your_cards_inactive',
  YourCardsActive = 'your_cards_active',
  AccountsYourAccounts = 'accounts_your_accounts',
  AccountsVirtualCards = 'accounts_virtual_cards',
  AccountOverview = 'account_overview',
  AccountVirtualCards = 'account_virtual_cards',
  BudgetsVirtualCards = 'budgets_virtual_cards',
  CreateVirtualCard = 'create_virtual_card',
  CreateBudget = 'create_budget',
  RequestVirtualCard = 'request_virtual_card',
  People = 'people',
  Onboarding = 'onboarding',
  FeatureFlagger = 'feature_flagger',
  NavBar = 'nav_bar',
  Transactions = 'transactions',
  Expenses = 'expenses',
  Unauthed = 'unauthed',
}

export enum CookieValuePaths {
  isRegisterAccountPromoOpen = 'is_register_account_promo_open',
  isCreateABudgetPromoOpen = 'is_create_a_budget_promo_open',
  isAddAnotherAccountPromoOpen = 'is_add_another_account_promo_open',
  isAddAnotherAccountHomePromoOpen = 'is_add_another_account_home_promo_open',
  firstLogin = 'first_login',
  shouldShowRequestCardPromo = 'should_show_request_card_promo',
  shouldShowAccountTutorialPromo = 'should_show_register_account_promo',
  shouldShowVirtualCardsPromo = 'should_show_virtual_cards_promo',
  shouldShowUpdateRoleListPromo = 'should_show_update_role_list_promo',
  bmoWelcomePopupOpen = 'bmo_welcome_popup_open',
  isIntegrationsPromoOpen = 'is_integrations_promo_open',
  shouldShowTransactionReconciliationPromo = 'should_show_transactions_reconciliation_promo',
  shouldShowBudgetVirtualCardsPromo = 'show_budget_virtual_cards_promo',
  coBrandWelcomePopupOpen = 'cobrand_welcome_popup_open',
  unauthedLocale = 'unauthed_locale',
  checkedSecondReportCycleDate = 'checked_second_report_cycle_date',
  shouldShowBulkEditTransactionsSpotlightPromo = 'should_show_bulk_edit_transactions_spotlight_promo',
  cachedAlertStats = 'cached_alert_stats',
  shouldShowRulesWhatsNew = 'should_show_rules_whats_new',
  shouldShowDowngradedToStarterModal = 'should_show_downgraded_to_starter_modal',
}

export const jwtCookieName = (envConfig: EnvConfig): string =>
  `extend_app_jwt_${envConfig.ENVIRONMENT}`
export const getJwtCookie = (
  envConfig: EnvConfig,
  cookie: string | undefined,
): string | undefined => {
  const cookies = new Cookies(cookie)
  return cookies.get(jwtCookieName(envConfig))
}
export const setJwtCookie = (envConfig: EnvConfig, jwt?: string): void => {
  const cookies = new Cookies()
  cookies.set(jwtCookieName(envConfig), jwt, persistentCookieOptions())
}

export const refreshTokenCookieName = (envConfig: EnvConfig): string =>
  `extend_app_refresh_token_${envConfig.ENVIRONMENT}`
export const getRefreshTokenCookie = (
  envConfig: EnvConfig,
  cookie: string | undefined,
): string | undefined => {
  const cookies = new Cookies(cookie)
  return cookies.get(refreshTokenCookieName(envConfig))
}
export const setRefreshTokenCookie = (
  envConfig: EnvConfig,
  refreshToken?: string,
): void => {
  const cookies = new Cookies()
  cookies.set(
    refreshTokenCookieName(envConfig),
    refreshToken,
    persistentCookieOptions(),
  )
}

export const cspJwtCookieName = (envConfig: EnvConfig): string =>
  `extend_app_csp_jwt_${envConfig.ENVIRONMENT}`
export const getCspJwtCookie = (
  envConfig: EnvConfig,
  cookie: string | undefined,
): string | undefined => {
  const cookies = new Cookies(cookie)
  return cookies.get(cspJwtCookieName(envConfig))
}
export const setCspJwtCookie = (envConfig: EnvConfig, jwt?: string): void => {
  const cookies = new Cookies()
  cookies.set(cspJwtCookieName(envConfig), jwt, persistentCookieOptions())
}

export const cspRefreshTokenCookieName = (envConfig: EnvConfig): string =>
  `extend_app_csp_refresh_token_${envConfig.ENVIRONMENT}`
export const getCspRefreshTokenCookie = (
  envConfig: EnvConfig,
  cookie: string | undefined,
): string | undefined => {
  const cookies = new Cookies(cookie)
  return cookies.get(cspRefreshTokenCookieName(envConfig))
}
export const setCspRefreshTokenCookie = (
  envConfig: EnvConfig,
  cspRefreshToken?: string,
): void => {
  const cookies = new Cookies()
  cookies.set(
    cspRefreshTokenCookieName(envConfig),
    cspRefreshToken,
    persistentCookieOptions(),
  )
}

export const userSessionTokenIdCookieName = (envConfig: EnvConfig): string =>
  `extend_app_token_id_${envConfig.ENVIRONMENT}`
export const getUserSessionTokenIdCookie = (
  envConfig: EnvConfig,
  cookie: string | undefined,
): string | undefined => {
  const cookies = new Cookies(cookie)
  return cookies.get(userSessionTokenIdCookieName(envConfig))
}
export const setUserSessionTokenIdCookie = (
  envConfig: EnvConfig,
  userSessionTokenId?: string,
): void => {
  const cookies = new Cookies()
  cookies.set(
    userSessionTokenIdCookieName(envConfig),
    userSessionTokenId,
    persistentCookieOptions(),
  )
}

export interface PerSessionValuesType {
  isBudgetWarningDismissed: boolean
  isConfirmOrgModalAccountAccountsDismissed: boolean
  isConfirmRCNOrBudgetModalAccountOverviewDismissed: boolean
  isConfirmBudgetModalAccountsBudgetsDismissed: boolean
  isConfirmBudgetModalCreateBudgetDismissed: boolean
  isConfirmBudgetModalUpdateBudgetDismissed: boolean
  isOrglessGuestCardWarningDismissed: boolean
  isOrgAdminUserWarningDismissed: boolean
  isAcceptOrgInvitePopupDismissed: boolean
  isOrglessUserWarningDismissed: boolean
  isBrandSuggestionPopupDismissed: boolean
}
export const defaultPerSessionValues = {
  isBudgetWarningDismissed: false,
  isConfirmOrgModalAccountAccountsDismissed: false,
  isConfirmRCNOrBudgetModalAccountOverviewDismissed: false,
  isConfirmBudgetModalAccountsBudgetsDismissed: false,
  isConfirmBudgetModalCreateBudgetDismissed: false,
  isConfirmBudgetModalUpdateBudgetDismissed: false,
  isOrglessGuestCardWarningDismissed: false,
  isOrgAdminUserWarningDismissed: false,
  isAcceptOrgInvitePopupDismissed: false,
  isOrglessUserWarningDismissed: false,
  isBrandSuggestionPopupDismissed: false,
}

export const perSessionValuesName = 'extend_app_per_session_values'

export const getPerSessionValuesCookie = (
  cookie?: string,
): PerSessionValuesType | undefined => {
  const cookies = new Cookies(cookie)
  return cookies.get(perSessionValuesName)
}
export const setPerSessionValuesCookie = (
  newValues: Partial<PerSessionValuesType>,
): void => {
  const cookies = new Cookies()
  cookies.set(perSessionValuesName, newValues, persistentCookieOptions())
}

export type TransactionsDownloadFields = {
  includeAttachments: boolean
  registeredCardLast4: boolean
  registeredCardName: boolean
  transactionDate: boolean
  clearDate: boolean
  type: boolean
  status: boolean
  nameOnCard: boolean
  vcnRecipientEmail: boolean
  authorizationMerchantCurrency: boolean
  authorizationMerchantAmount: boolean
  authorizationBillingCurrency: boolean
  authorizationBillingAmount: boolean
  authorizationExchangeRate: boolean
  settlementMerchantCurrency: boolean
  settlementMerchantAmount: boolean
  settlementBillingCurrency: boolean
  settlementBillingAmount: boolean
  settlementExchangeRate: boolean
  merchantName: boolean
  merchantAddress: boolean
  merchantCity: boolean
  merchantState: boolean
  merchantCountry: boolean
  merchantZipPostal: boolean
  mcc: boolean
  mccDescription: boolean
  vcnSender: boolean
  vcnSenderEmail: boolean
  vcnNickname: boolean
  vcnLast4: boolean
  referenceId: boolean
  source: boolean
  external: boolean
  attachments: boolean
  transactionAmount: boolean
  notes: boolean
  transactionReceipt: boolean
  receipt: boolean
  vendor: boolean
  customer: boolean
  expenseCategories: boolean
  cardholderEmail: boolean
  authBillingAmountCents: boolean
  authBillingCurrency: boolean
  authMerchantAmountCents: boolean
  authMerchantCurrency: boolean
  authExchangeRate: boolean
  attachmentsCount: boolean
  creditCardDisplayName: boolean
  creditCardType: boolean
  team: boolean
  managerReviewStatus: boolean
  additionalFields: boolean
}

export interface TransactionsCSVFields
  extends Omit<TransactionsDownloadFields, 'includeAttachments'> {}

export const transactionsDownloadFieldsName =
  'extend_app_transactions_download_fields'

export const getTransactionsDownloadFieldsCookie = (
  cookie?: string,
): TransactionsDownloadFields | undefined => {
  const cookies = new Cookies(cookie)
  return cookies.get(transactionsDownloadFieldsName)
}

export const setTransactionsDownloadFieldsCookie = (
  newValues: Partial<TransactionsDownloadFields>,
): void => {
  const cookies = new Cookies()
  const transactionDownloadFields = cookies.get(transactionsDownloadFieldsName)
  cookies.set(
    transactionsDownloadFieldsName,
    {
      ...transactionDownloadFields,
      ...newValues,
    },
    persistentCookieOptions(),
  )
}

export type VirtualCardsTableFields = {
  date: boolean
  cardName: boolean
  cardCreator: boolean
  cardUser: boolean
  cardType: boolean
  status: boolean
  activeUntil: boolean
  availableCredit: boolean
  details: boolean
}

export const virtualCardsTableFieldsName =
  'extend_app_virtual_cards_table_fields'

export const getVirtualCardsTableFieldsCookie = (
  cookie?: string,
): VirtualCardsTableFields | undefined => {
  const cookies = new Cookies(cookie)
  return cookies.get(virtualCardsTableFieldsName)
}

export const setVirtualCardsTableFieldsCookie = (
  newValues: Partial<VirtualCardsTableFields>,
): void => {
  const cookies = new Cookies()
  const virtualCardsTableFields = cookies.get(virtualCardsTableFieldsName)
  cookies.set(
    virtualCardsTableFieldsName,
    {
      ...virtualCardsTableFields,
      ...newValues,
    },
    persistentCookieOptions(),
  )
}
